<template>
    <div class="ai-md">
        <template v-for="(segment, index) in segments">
            <!-- eslint-disable-next-line vue/no-v-html -- HTML-escaped then whitelisted by renderMarkdown -->
            <div
                v-if="segment.kind === 'markdown'"
                :key="`md-${index}`"
                v-html="segment.html"
            ></div>
            <AiChatChart
                v-else-if="segment.kind === 'chart'"
                :key="`chart-${index}`"
                :spec="segment.spec"
            />
            <AiChatStats v-else :key="`stats-${index}`" :spec="segment.spec" />
        </template>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { parseAssistantContent } from "@/Pages/AiChat/markdown";

const props = defineProps<{
    content: string;
}>();

const segments = computed(() => parseAssistantContent(props.content));
</script>

<style scoped>
.ai-md {
    font-size: 0.875rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.ai-md :deep(p) {
    margin: 0 0 0.5rem;
}

.ai-md :deep(p:last-child) {
    margin-bottom: 0;
}

.ai-md :deep(h3),
.ai-md :deep(h4) {
    font-weight: 600;
    margin: 0.75rem 0 0.35rem;
    color: var(--color-gray-800);
}

.ai-md :deep(ul),
.ai-md :deep(ol) {
    margin: 0.25rem 0 0.5rem;
    padding-left: 1.25rem;
}

.ai-md :deep(ul) {
    list-style: disc;
}

.ai-md :deep(ol) {
    list-style: decimal;
}

.ai-md :deep(li) {
    margin: 0.1rem 0;
}

.ai-md :deep(strong) {
    font-weight: 600;
}

.ai-md :deep(code) {
    font-family:
        ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono",
        monospace;
    font-size: 0.8em;
    background: var(--color-gray-100);
    border-radius: 4px;
    padding: 0.1em 0.35em;
    word-break: break-all;
}

.ai-md :deep(pre) {
    background: var(--color-gray-100);
    border-radius: 8px;
    padding: 0.75rem;
    margin: 0.5rem 0;
    overflow-x: auto;
}

.ai-md :deep(pre code) {
    background: transparent;
    padding: 0;
    white-space: pre-wrap;
    word-break: break-word;
}

.ai-md :deep(.ai-md-table) {
    margin: 0.5rem 0;
    max-width: 100%;
    overflow-x: auto;
}

.ai-md :deep(table) {
    width: 100%;
    min-width: 26rem;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.ai-md :deep(th),
.ai-md :deep(td) {
    border: 1px solid var(--color-gray-200);
    padding: 0.35rem 0.5rem;
    text-align: left;
    vertical-align: top;
}

.ai-md :deep(th) {
    background: var(--color-gray-100);
    font-weight: 600;
    color: var(--color-gray-700);
}
</style>
